import { useEffect, useRef, useState } from 'react';
import { Button } from 'components/Button';
import { FormInput } from 'components/form/FormInput';
import { toMoney } from 'components/Money';
import { React, classNames } from 'libs/react';
import { FormSelect } from 'components/form/FormSelect';
import { useDealConfiguratorContext } from 'modules/deal/DealConfigureScreen';
import { FormDate } from 'components/form/FormDate';
import { DateTime } from 'components/DateTime';
import { Text } from 'components/Text';
import { Badge } from 'components/Badge';
import { FormGroup } from 'components/form/FormGroup';
import { FormField } from 'components/form/FormField';
import { Icon } from 'components/Icon';
import { useModal } from 'components/modal/ModalStack';
import { formatPercent } from 'helpers/format';
import { isProcessedPayment, paymentPercent, roundMoney } from 'helpers/DealPaymentSchedule';
import { DealPaymentDetails } from './DealPaymentDetails';
import './DealPaymentItem.scss';
function isPaymentDateEmpty(date) {
    if (date == null)
        return true;
    if (typeof date === 'string' && date.trim() === '')
        return true;
    return false;
}
/**
 * @component DealPaymentItem
 * @since 1.0.0
 */
export function DealPaymentItem(props) {
    var _a;
    const { deal, dealTotal, editable, payment, hasDeposit, showPaymentDetails } = props;
    const modal = useModal();
    const { updatePayments } = useDealConfiguratorContext();
    const total = dealTotal !== null && dealTotal !== void 0 ? dealTotal : 0;
    const percentMode = payment.percent != null;
    const percentValue = paymentPercent(payment, total);
    const processed = isProcessedPayment(payment);
    const locked = payment.status === 'COMPLETED';
    // Drafts let a partial value ("12.", "") be typed in the controlled inputs.
    const [percentText, setPercentText] = useState(String(percentValue));
    const [amountText, setAmountText] = useState(String(payment.amount));
    const percentRef = useRef(null);
    const dollarRef = useRef(null);
    const payments = (_a = deal === null || deal === void 0 ? void 0 : deal.payments) !== null && _a !== void 0 ? _a : [];
    useEffect(() => {
        if (parseFloat(percentText) !== percentValue) {
            setPercentText(String(percentValue));
        }
    }, [percentValue]);
    useEffect(() => {
        if (parseFloat(amountText) !== Number(payment.amount)) {
            setAmountText(String(payment.amount));
        }
    }, [payment.amount]);
    /**
     * Rows are matched by reference rather than by index, so a filtered list
     * (payments of one order) never edits the wrong payment.
     *
     * @function updateRow
     * @since 1.0.0
     * @hidden
     */
    const updateRow = (patch) => {
        updatePayments(payments.map((entry) => entry === payment ? Object.assign(Object.assign({}, entry), patch) : entry));
    };
    /**
     * @function onDepositChange
     * @since 1.0.0
     * @hidden
     */
    const onDepositChange = () => {
        const isDeposit = !payment.isDeposit;
        // A single payment is due at signing.
        updatePayments(payments.map((entry) => {
            if (entry === payment) {
                return Object.assign(Object.assign({}, entry), { isDeposit });
            }
            return isDeposit && entry.isDeposit && entry.status !== 'COMPLETED'
                ? Object.assign(Object.assign({}, entry), { isDeposit: false }) : entry;
        }));
    };
    /**
     * @function onItemRemove
     * @since 1.0.o
     * @hidden
     */
    const onItemRemove = () => {
        updatePayments(payments.filter((entry) => entry !== payment));
    };
    /**
     * @function onPaymentPercentChange
     * @since 1.0.0
     * @hidden
     */
    const onPaymentPercentChange = (value) => {
        setPercentText(value);
        const parsed = parseFloat(value);
        if (isNaN(parsed))
            return;
        const percent = Math.min(100, Math.max(0, parsed));
        updateRow({
            percent,
            amount: roundMoney((total * percent) / 100)
        });
    };
    /**
     * @function onPaymentDollarChange
     * @since 1.0.0
     * @hidden
     */
    const onPaymentDollarChange = (value) => {
        setAmountText(value);
        const parsed = parseFloat(value);
        if (isNaN(parsed))
            return;
        updateRow({ amount: parsed, percent: null });
    };
    /**
     * Switching to % keeps the current share and makes it follow the total.
     *
     * @function onPercentClick
     * @since 1.0.0
     * @hidden
     */
    const onPercentClick = () => {
        var _a;
        if (locked || percentMode)
            return;
        updateRow({ percent: percentValue });
        (_a = percentRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    /**
     * Switching to $ freezes the current amount.
     *
     * @function onDollarClick
     * @since 1.0.0
     * @hidden
     */
    const onDollarClick = () => {
        var _a;
        if (locked || !percentMode)
            return;
        updateRow({ percent: null });
        (_a = dollarRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    /**
     * @function onDetailsPress
     * @since 1.0.0
     * @hidden
     */
    const onDetailsPress = () => {
        modal.present({
            style: 'dialog',
            title: t('Payment details'),
            picto: 'credit-card',
            modal: React.createElement(DealPaymentDetails, { payment: payment })
        });
    };
    const className = classNames('deal-payment-item', {
        'deal-payment-item-completed': payment.status === 'COMPLETED',
        'deal-payment-item-inactive': payment.status === 'INACTIVE'
    });
    const quoteRowClassName = classNames('deal-payment-quote-row', {
        'deal-payment-quote-row--completed': payment.status === 'COMPLETED',
        'deal-payment-quote-row--inactive': payment.status === 'INACTIVE'
    });
    if (editable) {
        return (React.createElement("div", { className: className },
            React.createElement("div", { className: "deal-payment-item-container" },
                hasDeposit && (React.createElement("input", { type: "checkbox", className: "deal-payment-input-deposit", checked: payment.isDeposit === true, disabled: locked, onChange: onDepositChange })),
                payment.isDeposit === true ? (React.createElement(Text, { className: "deal-payment-input-date deal-payment-input-date--deposit-mention", value: t('At signing') })) : (React.createElement(FormDate, { name: "date", className: "deal-payment-input-date", size: "small", value: payment.date, time: false, disabled: locked, placeholder: isPaymentDateEmpty(payment.date)
                        ? t('To be confirmed')
                        : undefined, onChange: (date) => updateRow({ date }) })),
                React.createElement(FormInput, { name: "description", className: "deal-payment-input-description", size: "small", value: payment.description, placeholder: t('Description'), disabled: locked, onChange: (description) => updateRow({ description }) }),
                React.createElement(FormSelect, { name: "status", className: "deal-payment-input-status", size: "small", value: payment.status, options: [
                        { value: 'INACTIVE', label: t('Inactive') },
                        { value: 'ACTIVE', label: t('To pay') },
                        { value: 'COMPLETED', label: t('Completed') }
                    ], required: true, disabled: processed, onChange: (status) => updateRow({ status }) }),
                React.createElement(FormGroup, { className: "deal-payment-panel-amount-input-container" },
                    React.createElement(FormField, null,
                        React.createElement("div", { className: "deal-payment-panel-amount-input deal-payment-panel-amount-input--left", onClick: onPercentClick },
                            React.createElement(FormInput, { type: "number", size: "small", prefix: "%", onChange: percentMode ? onPaymentPercentChange : undefined, value: percentText, disabled: !percentMode || locked, className: percentMode ? 'isActive' : '', ref: percentRef }),
                            React.createElement(Icon, { className: "deal-payment-panel-amount-input-icon", value: percentMode ? 'angle-left' : 'angle-right' })),
                        React.createElement("div", { className: "deal-payment-panel-amount-input deal-payment-panel-amount-input--right", onClick: onDollarClick },
                            React.createElement(FormInput, { type: "number", size: "small", suffix: "$", onChange: percentMode ? undefined : onPaymentDollarChange, value: percentMode ? String(payment.amount) : amountText, disabled: percentMode || locked, className: percentMode ? '' : 'isActive', ref: dollarRef }))))),
            React.createElement("div", { className: "deal-payment-item-foot" }, !locked && !processed && (React.createElement(Button, { style: "picto", size: "small", picto: "trash-can", onPress: onItemRemove })))));
    }
    return (React.createElement("tr", { className: quoteRowClassName },
        hasDeposit && (React.createElement("td", { className: "deal-payment-quote-td deal-payment-quote-td--deposit" },
            React.createElement("div", { className: "deal-payment-quote-td-inner" },
                React.createElement("input", { type: "checkbox", className: "deal-payment-quote-deposit-cb", checked: payment.isDeposit === true, disabled: true, tabIndex: -1, "aria-label": t('Dépôt') })))),
        React.createElement("td", { className: "deal-payment-quote-td deal-payment-quote-td--date" },
            React.createElement("div", { className: "deal-payment-quote-td-inner" }, payment.isDeposit === true ? (React.createElement(Text, { className: "deal-payment-text-date deal-payment-text-date--mention", value: t('At signing') })) : isPaymentDateEmpty(payment.date) ? (React.createElement(Text, { className: "deal-payment-text-date deal-payment-text-date--mention", value: t('To be confirmed') })) : (React.createElement(DateTime, { value: payment.date, className: "deal-payment-text-date", y: "numeric", m: "numeric", d: "numeric" })))),
        React.createElement("td", { className: "deal-payment-quote-td deal-payment-quote-td--description" },
            React.createElement("div", { className: "deal-payment-quote-td-inner" },
                React.createElement(Text, { value: payment.description, className: "deal-payment-text-description" }))),
        React.createElement("td", { className: "deal-payment-quote-td deal-payment-quote-td--status" },
            React.createElement("div", { className: "deal-payment-quote-td-inner" },
                React.createElement(Badge, { className: "deal-payment-badge", style: "success", label: payment.status === 'INACTIVE'
                        ? t('To come')
                        : payment.status === 'ACTIVE'
                            ? t('To pay')
                            : t('Succeeded') }))),
        React.createElement("td", { className: "deal-payment-quote-td deal-payment-quote-td--percent" },
            React.createElement("div", { className: "deal-payment-quote-td-inner" },
                React.createElement(Text, { value: formatPercent(percentValue, 2), className: "deal-payment-text-percent" }))),
        React.createElement("td", { className: "deal-payment-quote-td deal-payment-quote-td--price" },
            React.createElement("div", { className: "deal-payment-quote-td-inner" },
                React.createElement(Text, { value: toMoney(payment.amount), className: "deal-payment-text-price" }))),
        showPaymentDetails && (React.createElement("td", { className: "deal-payment-quote-td deal-payment-quote-td--details" },
            React.createElement("div", { className: "deal-payment-quote-td-inner" },
                React.createElement(Button, { style: "picto", size: "small", picto: "circle-info", className: "deal-payment-details-button", onPress: onDetailsPress }))))));
}
